import { useTranslation } from 'react-i18next';
import { map } from 'lodash';
import { Container } from '@dagensmat/carrot/Layout';
import { Callout, Typography } from '@dagensmat/carrot/Components';
import {
  PRODUCER_DELIVERS,
  PRODUCER_TRANSPORTS,
  hasSomeOrderNotLeftProducer,
  hasAllOrdersLeftProducer
} from 'utils/delivery';
import { formatDate } from 'utils/date/format';
import { postLeftProducer } from 'api';
import { useAppSelector, useAppDispatch } from '_common/hooks/reduxHooks';
import ConfirmButton from '_common/components/confirm-button/ConfirmButton';
import { fetchOrders } from '_common/reducers/orders';
import type { IOrderGroup } from './OrdersByDeliveryPage';

const DeliveryInfoText = ({ orderGroup }: { orderGroup: IOrderGroup }) => {
  const { t } = useTranslation();
  const {
    type,
    toName,
    orders = [],
    producerDeliveryDate,
    toDeadline
  } = orderGroup;

  if (hasAllOrdersLeftProducer(orders)) {
    return (
      <Callout colour="lightGrey" mt="s">
        {t('common:deliveryInProgressCallout')}
      </Callout>
    );
  }

  if (type === PRODUCER_DELIVERS) {
    return (
      <Typography variant="paragraph" mt="s">
        {t('producer:YouDeliverDirectlyToTheCustomer', {
          count: orders.length
        })}
      </Typography>
    );
  }

  if (type === PRODUCER_TRANSPORTS) {
    return toDeadline ? (
      <Typography variant="paragraph" mt="s">
        {t('producer:DropOffAtWithDeadline', {
          toName,
          date: formatDate(producerDeliveryDate),
          deadline: toDeadline
        })}
      </Typography>
    ) : (
      <Typography variant="paragraph" mt="s">
        {t('producer:DropOffAt', {
          toName,
          date: formatDate(producerDeliveryDate)
        })}
      </Typography>
    );
  }

  return null;
};

const ProducerActions = ({ orderGroup }: { orderGroup: IOrderGroup }) => {
  const { t } = useTranslation();
  const userId = useAppSelector(({ auth: { _id } }) => {
    return _id;
  });
  const dispatch = useAppDispatch();

  const { orders = [], type: pickupType } = orderGroup || {};

  const producerActionNeeded = hasSomeOrderNotLeftProducer(orders);

  const deliveryVerb = {
    [PRODUCER_DELIVERS]: t('common:delivered'),
    [PRODUCER_TRANSPORTS]: t('common:droppedOff')
  }[pickupType];

  return (
    <>
      <DeliveryInfoText orderGroup={orderGroup} />
      {producerActionNeeded && (
        <Container my="s">
          <ConfirmButton
            buttonText={t('producer:MarkAsDelivered', { deliveryVerb })}
            confirmText={t('producer:AreOrders', {
              deliveryVerb,
              count: orders.length
            })}
            confirmedText=""
            rejectButtonText={t('producer:NotYetDelivered')}
            confirmButtonText={t('producer:YesDelivered', { deliveryVerb })}
            onConfirm={async () => {
              await postLeftProducer({ orderIds: map(orders, '_id') });
              dispatch(
                fetchOrders({ producerId: userId }, { clearStore: false })
              );
            }}
            buttonVariant="primary"
          />
        </Container>
      )}
    </>
  );
};

export default ProducerActions;
